import { cn } from "@/lib/utils"
import { Calculator, Lightbulb, Trophy, ThumbsUp, TrendingUp, AlertTriangle } from "lucide-react"
import type { ReactNode } from "react"

// ─── Section Heading ──────────────────────────────────────────
export function SectionHeading({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h2 className={cn("text-3xl sm:text-4xl font-extrabold text-neutral-900 dark:text-white mb-5 tracking-tight leading-[1.15]", className)}>
      {children}
    </h2>
  )
}

export function SubHeading({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={cn("text-xl sm:text-2xl font-bold text-neutral-800 dark:text-neutral-100 mt-8 mb-3 tracking-tight", className)}>
      {children}
    </h3>
  )
}

export function Paragraph({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <p className={cn("text-neutral-600 dark:text-neutral-400 leading-[1.8] text-base", className)}>
      {children}
    </p>
  )
}

// ─── Numbered Steps ───────────────────────────────────────────
type Step = { title: string; description: string }

export function NumberedSteps({ steps }: { steps: Step[] }) {
  return (
    <div className="space-y-6 mt-6">
      {steps.map((step, i) => (
        <div key={i} className="flex gap-5 items-start">
          <div className="relative flex-shrink-0">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-accent to-brand-accent-light flex items-center justify-center text-white font-extrabold text-base shadow-[0_8px_20px_-6px_rgba(244,63,94,0.5)] ring-4 ring-rose-50 dark:ring-rose-950/30">
              {i + 1}
            </div>
          </div>
          <div className="flex-1 pt-1.5">
            <p className="font-bold text-neutral-900 dark:text-white text-base sm:text-lg mb-1.5">{step.title}</p>
            <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.75]">{step.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Feature/Method Grid ──────────────────────────────────────
type FeatureCard = { title: string; description: string; icon?: string | ReactNode }

export function FeatureGrid({ items, columns = 2 }: { items: FeatureCard[]; columns?: 2 | 3 }) {
  return (
    <div className={cn(
      "grid gap-5 mt-6",
      columns === 3 ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3" : "grid-cols-1 sm:grid-cols-2"
    )}>
      {items.map((item, i) => (
        <div
          key={i}
          className="group relative rounded-2xl border border-neutral-100 dark:border-neutral-800/80 bg-gradient-to-br from-rose-50/40 via-white to-white dark:from-rose-950/10 dark:via-neutral-900/60 dark:to-neutral-900/60 p-6 hover:-translate-y-1.5 hover:shadow-[0_15px_40px_-10px_rgba(244,63,94,0.18)] dark:hover:shadow-[0_15px_40px_-10px_rgba(244,63,94,0.3)] hover:border-brand-accent/30 transition-all duration-300"
        >
          {item.icon && (
            typeof item.icon === "string"
              ? (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-100 to-rose-50 dark:from-rose-900/30 dark:to-rose-950/20 flex items-center justify-center text-3xl mb-4 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
              )
              : (
                <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-rose-100 to-rose-50 dark:from-rose-900/30 dark:to-rose-950/20 flex items-center justify-center text-brand-accent mb-4 group-hover:scale-110 transition-transform duration-300">
                  {item.icon}
                </div>
              )
          )}
          <p className="font-bold text-neutral-900 dark:text-white text-lg mb-2 tracking-tight">{item.title}</p>
          <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.7]">{item.description}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Stat Highlight Cards ─────────────────────────────────────
type Stat = { value: string; label: string; sublabel?: string; icon?: ReactNode }

export function StatGrid({ stats }: { stats: Stat[] }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
      {stats.map((stat, i) => (
        <div
          key={i}
          className="rounded-2xl border border-neutral-100 dark:border-neutral-800/80 bg-gradient-to-br from-rose-50/40 via-white to-white dark:from-rose-950/10 dark:via-neutral-900/60 dark:to-neutral-900/60 p-5 text-center hover:-translate-y-1 hover:shadow-[0_10px_30px_-10px_rgba(244,63,94,0.2)] transition-all duration-300"
        >
          {stat.icon && (
            <div className="flex justify-center mb-3 text-brand-accent">
              {stat.icon}
            </div>
          )}
          <p className="text-3xl sm:text-4xl font-extrabold bg-gradient-to-r from-brand-accent to-brand-accent-light bg-clip-text text-transparent">
            {stat.value}
          </p>
          <p className="text-sm font-bold text-neutral-800 dark:text-neutral-200 mt-1.5">{stat.label}</p>
          {stat.sublabel && (
            <p className="text-xs text-neutral-500 dark:text-neutral-500 mt-0.5">{stat.sublabel}</p>
          )}
        </div>
      ))}
    </div>
  )
}

// ─── Formula / Code Box ───────────────────────────────────────
export function FormulaBox({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <div className={cn(
      "relative rounded-2xl bg-gradient-to-br from-rose-50 via-rose-50/60 to-white dark:from-rose-950/20 dark:via-rose-950/10 dark:to-neutral-900/40 border border-rose-100 dark:border-rose-900/30 p-6 sm:p-7 mt-5 overflow-hidden",
      className
    )}>
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-brand-accent to-brand-accent-light" />
      <div className="flex items-center gap-2 text-[11px] font-bold text-brand-accent uppercase tracking-[0.15em] mb-4">
        <div className="w-6 h-6 rounded-md bg-brand-accent/10 flex items-center justify-center">
          <Calculator className="w-3.5 h-3.5" />
        </div>
        Formula
      </div>
      <code className="text-lg sm:text-xl text-brand-accent font-bold font-mono block leading-[1.6] break-words">
        {children}
      </code>
    </div>
  )
}

// ─── Example / Callout Box ────────────────────────────────────
export function ExampleBox({ title, children }: { title?: string; children: ReactNode }) {
  return (
    <div className="relative rounded-xl bg-white dark:bg-neutral-900/70 border border-neutral-200/80 dark:border-neutral-800 p-5 mt-4 shadow-[0_4px_12px_-4px_rgba(0,0,0,0.05)] overflow-hidden">
      <div className="absolute left-0 top-0 bottom-0 w-1 bg-amber-400" />
      {title && (
        <div className="flex items-center gap-2.5 mb-2.5">
          <div className="w-6 h-6 rounded-md bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center flex-shrink-0">
            <Lightbulb className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
          </div>
          <p className="font-bold text-neutral-800 dark:text-neutral-200 text-[15px]">{title}</p>
        </div>
      )}
      <div className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.75] pl-0.5">
        {children}
      </div>
    </div>
  )
}

// ─── Tier / Benchmark Cards ───────────────────────────────────
type TierCard = { label: string; range: string; description: string; color: "green" | "blue" | "yellow" | "red" }

const tierColors = {
  green: "border-l-emerald-500 bg-gradient-to-r from-emerald-50 via-emerald-50/40 to-white dark:from-emerald-950/20 dark:via-emerald-950/10 dark:to-neutral-900/40",
  blue: "border-l-blue-500 bg-gradient-to-r from-blue-50 via-blue-50/40 to-white dark:from-blue-950/20 dark:via-blue-950/10 dark:to-neutral-900/40",
  yellow: "border-l-amber-500 bg-gradient-to-r from-amber-50 via-amber-50/40 to-white dark:from-amber-950/20 dark:via-amber-950/10 dark:to-neutral-900/40",
  red: "border-l-rose-500 bg-gradient-to-r from-rose-50 via-rose-50/40 to-white dark:from-rose-950/20 dark:via-rose-950/10 dark:to-neutral-900/40",
}

const tierIconBg = {
  green: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400",
  blue: "bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400",
  yellow: "bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400",
  red: "bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400",
}

const tierTextColors = {
  green: "text-emerald-700 dark:text-emerald-400",
  blue: "text-blue-700 dark:text-blue-400",
  yellow: "text-amber-700 dark:text-amber-400",
  red: "text-rose-700 dark:text-rose-400",
}

const tierPillColors = {
  green: "bg-emerald-100/80 text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300",
  blue: "bg-blue-100/80 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",
  yellow: "bg-amber-100/80 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300",
  red: "bg-rose-100/80 text-rose-700 dark:bg-rose-900/40 dark:text-rose-300",
}

const tierIcons = {
  green: <Trophy className="w-5 h-5" />,
  blue: <ThumbsUp className="w-5 h-5" />,
  yellow: <TrendingUp className="w-5 h-5" />,
  red: <AlertTriangle className="w-5 h-5" />,
}

export function TierCards({ tiers }: { tiers: TierCard[] }) {
  return (
    <div className="space-y-4 mt-6">
      {tiers.map((tier, i) => (
        <div key={i} className={cn("rounded-2xl border border-neutral-100 dark:border-neutral-800/80 border-l-[5px] p-5 sm:p-6 hover:shadow-md transition-shadow duration-300", tierColors[tier.color])}>
          <div className="flex items-center gap-3 mb-2.5 flex-wrap">
            <div className={cn("w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0", tierIconBg[tier.color])}>
              {tierIcons[tier.color]}
            </div>
            <span className={cn("text-xl font-extrabold", tierTextColors[tier.color])}>{tier.label}</span>
            <span className={cn("text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full", tierPillColors[tier.color])}>{tier.range}</span>
          </div>
          <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.75]">{tier.description}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Use Case Badge Cards ─────────────────────────────────────
type UseCase = { badge: string; badgeColor: string; title: string; description: string }

export function UseCaseCards({ cases }: { cases: UseCase[] }) {
  return (
    <div className="space-y-5 mt-6">
      {cases.map((uc, i) => (
        <div key={i} className="rounded-2xl border border-neutral-100 dark:border-neutral-800/80 bg-gradient-to-br from-rose-50/30 via-white to-white dark:from-rose-950/10 dark:via-neutral-900/60 dark:to-neutral-900/60 p-6 hover:border-brand-accent/30 hover:-translate-y-1 hover:shadow-[0_12px_30px_-10px_rgba(244,63,94,0.18)] transition-all duration-300">
          <span className={cn("inline-block px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-[0.1em] mb-3", uc.badgeColor)}>
            {uc.badge}
          </span>
          <p className="font-bold text-neutral-900 dark:text-white text-lg mb-2 tracking-tight">{uc.title}</p>
          <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.7]">{uc.description}</p>
        </div>
      ))}
    </div>
  )
}

// ─── Comparison Table ─────────────────────────────────────────
type ComparisonRow = { method: string; values: string[]; highlight?: boolean }

export function ComparisonTable({ headers, rows }: { headers: string[]; rows: ComparisonRow[] }) {
  return (
    <div className="rounded-2xl border border-neutral-100 dark:border-neutral-800/80 overflow-hidden mt-6 shadow-[0_4px_16px_-6px_rgba(0,0,0,0.06)]">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gradient-to-r from-rose-50/60 to-neutral-50 dark:from-rose-950/10 dark:to-neutral-800/40">
            {headers.map((h, i) => (
              <th key={i} className="text-left px-5 py-4 font-bold text-neutral-700 dark:text-neutral-300 text-xs uppercase tracking-wider">
                {h}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={i}
              className={cn(
                "border-t border-neutral-100 dark:border-neutral-800 transition-colors duration-200",
                row.highlight
                  ? "bg-rose-50/50 dark:bg-rose-900/10 hover:bg-rose-50 dark:hover:bg-rose-900/15"
                  : "hover:bg-neutral-50/60 dark:hover:bg-neutral-800/30"
              )}
            >
              <td className={cn("px-5 py-4", row.highlight ? "font-bold text-neutral-900 dark:text-white" : "text-neutral-700 dark:text-neutral-300 font-medium")}>
                {row.method}
              </td>
              {row.values.map((v, j) => (
                <td key={j} className={cn("px-5 py-4", row.highlight ? "font-semibold text-neutral-900 dark:text-white" : "text-neutral-600 dark:text-neutral-400")}>
                  {v}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}

// ─── Tip List ─────────────────────────────────────────────────
type Tip = { title: string; description: string }

export function TipList({ tips }: { tips: Tip[] }) {
  return (
    <div className="space-y-6 mt-6">
      {tips.map((tip, i) => (
        <div key={i} className="flex gap-5 items-start">
          <div className="relative flex-shrink-0">
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-brand-accent to-brand-accent-light flex items-center justify-center text-white font-extrabold text-base shadow-[0_8px_20px_-6px_rgba(244,63,94,0.5)] ring-4 ring-rose-50 dark:ring-rose-950/30">
              {i + 1}
            </div>
          </div>
          <div className="flex-1 pt-1.5">
            <p className="font-bold text-neutral-900 dark:text-white text-base sm:text-lg mb-1.5">{tip.title}</p>
            <p className="text-neutral-600 dark:text-neutral-400 text-[15px] leading-[1.75]">{tip.description}</p>
          </div>
        </div>
      ))}
    </div>
  )
}

// ─── Content Section Wrapper ──────────────────────────────────
export function ContentSection({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <section className={cn(
      "relative rounded-3xl bg-white dark:bg-neutral-900/50 border border-neutral-100 dark:border-neutral-800/70 p-7 sm:p-10 lg:p-12 shadow-[0_4px_24px_-8px_rgba(0,0,0,0.06)] dark:shadow-[0_4px_24px_-8px_rgba(0,0,0,0.3)] overflow-hidden",
      className
    )}>
      {/* Top accent bar */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-brand-accent via-brand-accent-light to-brand-button" />
      {children}
    </section>
  )
}

// ─── Re-export icons for tool pages ───────────────────────────
export { Sparkles, Star, CheckCircle2, TrendingUp, Trophy, Calculator, Lightbulb, AlertTriangle, ThumbsUp } from "lucide-react"
export {
  BarChart3, Target, Users, Zap, Clock, DollarSign, Eye, Heart,
  MessageCircle, Search, Shield, Smartphone, Globe, Lock, Mail,
  PenTool, Repeat, Share2, ShoppingBag, Bookmark, ArrowUpRight,
} from "lucide-react"
